import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { findIndex, size } from 'lodash';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';

import {
  fetchSearch,
  PreviousNextNavigationDirection,
  PreviousNextPagination,
  Toast,
} from '@app/components';
import { useSearchContext } from '@app/contexts';
import { useInstanceDetailsRoutePath, useQueryParams } from '@app/hooks';
import { setPageRecords } from '@app/utils';
import { SetState, totalRecordsLimit } from '@app/constants';

export type PreviousNextInstancePaginationPage = { id: string }[] | undefined;

type PreviousNextPaginationState = {
  instanceNumber: number;
  // pageNumber must only be set when instance is not on current page
  previous: { id: string | null; pageNumber?: number };
  next: { id: string | null; pageNumber?: number };
};

interface PreviousNextInstancePaginationProps {
  instanceId: string;
  pages: PreviousNextInstancePaginationPage[];
  setPages: SetState<PreviousNextInstancePaginationPage[]>;
}

export const PreviousNextInstancePagination: FC<
  PreviousNextInstancePaginationProps
> = ({ instanceId, pages, setPages }) => {
  const navigate = useNavigate();
  const { $t } = useIntl();
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const queryParams = useQueryParams();
  const {
    state: searchState,
    searchResults: { totalRecords: actualTotalRecords },
  } = useSearchContext();
  const [loadingPageDirection, setLoadingPageDirection] =
    useState<PreviousNextNavigationDirection>();

  const pageNumber = Number(queryParams.pageNumber);
  const recordsPerPage = Number(queryParams.recordsPerPage);
  const currentPage = pages?.[pageNumber - 1];
  const isMoreThanOneInstanceOnPage = currentPage && size(currentPage) > 1;
  const instanceIndex = findIndex(
    currentPage,
    instance => instance.id === instanceId
  );
  const isCurrentInstanceOnPage = instanceIndex !== -1;
  const isSearchExecuted =
    [pageNumber, recordsPerPage].every(value => !Number.isNaN(value)) &&
    actualTotalRecords;

  if (
    !isSearchExecuted ||
    !isMoreThanOneInstanceOnPage ||
    !isCurrentInstanceOnPage
  ) {
    return null;
  }

  const limitedTotalRecords = Math.min(actualTotalRecords, totalRecordsLimit);

  const getPaginationState = (): PreviousNextPaginationState => {
    const prevIndex = instanceIndex - 1;
    const nextIndex = instanceIndex + 1;
    const instanceNumber =
      (pageNumber - 1) * recordsPerPage + instanceIndex + 1;
    const isFirstInstanceOnPage = prevIndex === -1;

    if (isFirstInstanceOnPage) {
      if (pageNumber === 1) {
        return {
          instanceNumber,
          previous: { id: null },
          next: { id: currentPage[nextIndex].id },
        };
      }

      return {
        instanceNumber,
        previous: {
          id: pages[pageNumber - 2]?.[recordsPerPage - 1]?.id ?? null,
          pageNumber: pageNumber - 1,
        },
        next: {
          id: currentPage[nextIndex].id,
        },
      };
    }

    const isLastPage =
      Math.min(limitedTotalRecords, pageNumber * recordsPerPage) <=
      limitedTotalRecords;
    const isNextInstanceLast =
      isLastPage && instanceNumber + 1 === limitedTotalRecords;

    if (nextIndex === recordsPerPage && !isNextInstanceLast) {
      return {
        instanceNumber,
        previous: {
          id: currentPage[prevIndex].id,
        },
        next: {
          id: pages[pageNumber]?.[0]?.id ?? null,
          pageNumber: pageNumber + 1,
        },
      };
    }

    return {
      instanceNumber,
      previous: { id: currentPage[prevIndex]?.id },
      next: { id: currentPage[nextIndex]?.id },
    };
  };

  const handlePaginationNavigation = (
    state: PreviousNextPaginationState,
    direction: PreviousNextNavigationDirection
  ) => {
    const config = state[direction];
    const navigationOptions = config.pageNumber
      ? { pageNumber: config.pageNumber }
      : {};

    const isInstanceFromSiblingPageThatWasNotFetched =
      config.pageNumber && !config.id;

    if (isInstanceFromSiblingPageThatWasNotFetched) {
      const isPrev = direction === 'previous';

      setLoadingPageDirection(direction);

      fetchSearch({
        ...searchState,
        pageNumber: config.pageNumber,
        recordsPerPage,
        limit: recordsPerPage,
        ...(isPrev && {
          offset: recordsPerPage * ((config.pageNumber as number) - 1),
        }),
      })
        .then(result => {
          setPageRecords({
            pageNumber: config.pageNumber as number,
            records: result?.instances,
            setPages,
          });

          const instanceIndex = isPrev ? recordsPerPage - 1 : 0;
          const instanceId = result?.instances?.[instanceIndex]?.id;

          if (instanceId) {
            navigate(
              getInstanceDetailsRoutePath(instanceId, navigationOptions)
            );
          }
        })
        .catch(() => {
          Toast({
            type: 'error',
            title: $t({
              id: 'previousNextInstancePagination.somethingWentWrong',
              defaultMessage:
                "We've encountered an error loading the page. Please try again",
            }),
            icon: faTimesCircle,
          });
        })
        .finally(() => {
          setLoadingPageDirection(undefined);
        });

      return;
    }

    if (config.id) {
      navigate(getInstanceDetailsRoutePath(config.id, navigationOptions));
    }
  };

  const paginationState = getPaginationState();

  return (
    <PreviousNextPagination
      pageNumber={paginationState.instanceNumber}
      limitedTotalRecords={limitedTotalRecords}
      actualTotalRecords={actualTotalRecords}
      loadingPageDirection={loadingPageDirection}
      onPrevious={() => handlePaginationNavigation(paginationState, 'previous')}
      onNext={() => handlePaginationNavigation(paginationState, 'next')}
    />
  );
};
